var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import axios from 'axios';
import { useUXPContext } from '@core/context/useUXPContext';
import { generateUUID } from '@utils';
import { uploadChunked } from './chunkedUpload';
import { getRequestErrorMessage } from '@core/context/executionHelpers';
export function normalizePath(path) {
    let p = (path || '').trim();
    if (p.startsWith('/'))
        p = p.slice(1);
    if (p.length > 0 && !p.endsWith('/'))
        p += '/';
    return p;
}
export function toAbsolute(url) {
    if (/^https?:\/\//i.test(url))
        return url;
    if (typeof window === 'undefined')
        return url;
    return window.location.origin + (url.startsWith('/') ? url : '/' + url);
}
/**
 * @export
 * Largest file a picker should accept, in MB. 0 means no limit.
 *
 * The account's size cap applies to single-request uploads only. When chunking
 * is switched on, anything above the chunk threshold is split across requests
 * and the cap does not apply to it, so there is no size limit at all. This
 * matches how v4 wired the same two settings.
 */
export function getUploadSizeLimitMB(context) {
    // The framework never told us about upload settings, so it predates them.
    // It still sends maxFileUploadSize, but nothing enforced that on the client
    // before, and starting now would refuse files that upload fine there.
    if ((context === null || context === void 0 ? void 0 : context.largeUploadFileSizeMB) === undefined)
        return 0;
    const maxSizeMB = (context === null || context === void 0 ? void 0 : context.maxFileUploadSize) || 0;
    const thresholdMB = (context === null || context === void 0 ? void 0 : context.largeUploadFileSizeMB) || 0;
    // Chunking on: files above the threshold have no cap, and the threshold is
    // at or below the cap in any sane config, so nothing is left to enforce.
    if (thresholdMB > 0)
        return 0;
    return maxSizeMB;
}
/**
 * @export
 * Upload a file to the platform content store.
 *
 * Small files are POSTed as multipart form data to
 * `{lucyUrl}uploadcontent/{path}?filename={name}`. Files above the account's
 * large-upload threshold are sent to the chunked endpoint instead, which is the
 * only way past the server's single-request size limit. Callers do not choose:
 * the size decides, and the result is the same either way.
 *
 * Either way the stored file is served from `{lucyUrl}content/{path}{name}`
 * (returned as an absolute URL).
 *
 * Prefer the `useFileUpload` hook inside components; this function exists for
 * non-hook contexts.
 */
export function uploadFile(context, file, options) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!(context === null || context === void 0 ? void 0 : context.lucyUrl))
            throw new Error('uploadFile: UXP context has no lucyUrl');
        if (!file)
            throw new Error('uploadFile: no file given');
        const path = normalizePath(options === null || options === void 0 ? void 0 : options.path);
        const ext = file.name.includes('.') ? file.name.split('.').pop() : 'bin';
        const fileName = (options === null || options === void 0 ? void 0 : options.fileName) || `file-${generateUUID()}.${ext}`;
        const base = context.lucyUrl.endsWith('/') ? context.lucyUrl : context.lucyUrl + '/';
        // A threshold of 0 means never chunk, which is both the shipped default and
        // the behaviour every existing account has today.
        const thresholdMB = context.largeUploadFileSizeMB || 0;
        if (thresholdMB > 0 && file.size > thresholdMB * 1024 * 1024) {
            yield uploadChunked(context, file, { path, fileName, onProgress: options === null || options === void 0 ? void 0 : options.onProgress, params: options === null || options === void 0 ? void 0 : options.params });
            return { url: toAbsolute(`${base}content/${path}${fileName}`), fileName };
        }
        // Single-request uploads only. The chunked path above has no size cap, the
        // same as v4: the request is split, so the limit does not apply to it.
        const maxSizeMB = getUploadSizeLimitMB(context);
        if (maxSizeMB > 0 && file.size > maxSizeMB * 1024 * 1024) {
            throw new Error(`File must be smaller than ${maxSizeMB} MB`);
        }
        const formData = new FormData();
        formData.append('file', file);
        Object.entries((options === null || options === void 0 ? void 0 : options.params) || {}).forEach(([key, value]) => formData.append(key, value));
        try {
            yield axios.post(`${base}uploadcontent/${path}?filename=${encodeURIComponent(fileName)}`, formData, {
                headers: { Authorization: context.apiKey },
                // `total` is undefined when the response has no content-length (e.g. gzipped
                // request streams), in which case there is no percentage to report
                onUploadProgress: (e) => {
                    if ((options === null || options === void 0 ? void 0 : options.onProgress) && e.total)
                        options.onProgress(Math.round((e.loaded * 100) / e.total));
                },
            });
        }
        catch (e) {
            // Surface the server's reason (a validation failure, the size cap)
            // rather than axios's "Request failed with status code 400".
            throw new Error(getRequestErrorMessage(e));
        }
        return { url: toAbsolute(`${base}content/${path}${fileName}`), fileName };
    });
}
/**
 * @export
 * Reusable file-upload hook for the platform content store, so widgets don't
 * hand-roll the upload flow.
 *
 * @example
 * ```tsx
 * const { upload, isUploading, progress, error } = useFileUpload();
 *
 * async function onFile(file: File) {
 *     const { url } = await upload(file, { path: 'notes/uploads/images/' });
 *     setImageUrl(url);
 * }
 * ```
 */
export function useFileUpload() {
    const context = useUXPContext();
    const [isUploading, setIsUploading] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [error, setError] = React.useState(null);
    const upload = React.useCallback((file, options) => __awaiter(this, void 0, void 0, function* () {
        setIsUploading(true);
        setProgress(0);
        setError(null);
        try {
            return yield uploadFile(context, file, Object.assign(Object.assign({}, options), { onProgress: (percent) => {
                    var _a;
                    setProgress(percent);
                    (_a = options === null || options === void 0 ? void 0 : options.onProgress) === null || _a === void 0 ? void 0 : _a.call(options, percent);
                } }));
        }
        catch (e) {
            const msg = (e === null || e === void 0 ? void 0 : e.message) || 'Upload failed';
            setError(msg);
            throw e;
        }
        finally {
            setIsUploading(false);
        }
    }), [context]);
    return { upload, isUploading, progress, error };
}
export default useFileUpload;
